import {CompanyEntity} from "../../../model/company";
import {Site, SiteEntity} from "../../../model/site";
import {contains} from "../../../util/util";
import {getObjectId} from "../../../model/entity";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";

export type SiteSelectorProps = {
    regionId?: string
    company?: CompanyEntity | string;
    showDoNotUse?: boolean;
};

const getRegionIdParam = createParamSelector<SiteSelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCompanyIdParam = createParamSelector<SiteSelectorProps | undefined, string | undefined>(params => getObjectId(params?.company));
const getShowDoNotUseParam = createParamSelector<SiteSelectorProps | undefined, boolean | undefined>(params => params?.showDoNotUse);

export const sitesSelector = () => createSelector(
    [getUseQueryResultData<Site[]>, getRegionIdParam, getCompanyIdParam, getShowDoNotUseParam],
    (data, regionId, companyId, showDoNotUse) => {
        return selectSitesAndTransform(
            data ?? [], {
                regionId: regionId,
                company: companyId,
                showDoNotUse: showDoNotUse
            }
        );
    }
);

export const siteSelector = () => createSelector(
    [getUseQueryResultData<Site>],
    (data) => {
        return data ? transformSite(data) : undefined;
    }
);

export const selectSites = (values?: Site[], selector?: SiteSelectorProps): Site[] => {
    if (!values) {
        return [];
    }

    return values.filter((v) => {
        let filterIn = true;

        if (selector?.regionId) {
            if (!contains(v.regionList, id => id === selector.regionId)) {
                filterIn = false;
            }
        }

        if (selector?.company) {
            const companyId = getObjectId(selector.company);
            if (companyId !== v.companyId) {
                filterIn = false;
            }
        }

        const showDoNotUse = selector?.showDoNotUse ?? false;
        if (filterIn && !showDoNotUse && v.doNotUse) {
            filterIn = false;
        }

        return filterIn;
    });
};

export const transformSite = (value: Site): SiteEntity => {
    return SiteEntity.from(value)
}

export const transformSites = (values: Site[]): SiteEntity[] => {
    return values?.map(transformSite) ?? []
}

export const selectSitesAndTransform = (values?: Site[], selector?: SiteSelectorProps): SiteEntity[] => {
    const selected = selectSites(values, selector);
    return selected.map(transformSite);
};
